import React, { useState } from 'react';

import Alert from 'CommonComponents/Alert';
import TextButton from 'CommonComponents/TextButton';

import ArrowUturnLeftIcon from 'Assets/icons/arrow-uturn-left.svg';

/**
 * What the last bulk apply on this table did, and the one action that reverses it.
 *
 * Undo stays offered until the next bulk apply on the same table or a page
 * reload — the plan it reverts is cached server-side with a matching lifetime.
 */
const SKIP_REASONS = () => ({
    split_row: $t('Split across several allocations'),
    has_value: $t('Already had a different value'),
    already_set: $t('Already had this value'),
    invalid_value: $t('Not a valid option for the row'),
    changed_elsewhere: $t('Changed by someone else'),
    would_unreconcile: $t('Would have dropped off the reconciled list'),
    edited_since: $t('Edited since the apply'),
    deleted: $t('Deleted since the apply'),
});

export default function BulkApplyResultBar({ result, undoUrl, onUndone, onDismiss, toast }) {
    const [undoing, setUndoing] = useState(false);
    const [showSkipped, setShowSkipped] = useState(false);

    const reasons = SKIP_REASONS();
    const applied = result.applied?.length ?? 0;
    const skipped = result.skipped ?? [];

    // One line per reason rather than per row: a 5000-row action can skip
    // hundreds, and "412 already had a value" is the useful form of that.
    const skippedByReason = skipped.reduce((carry, item) => {
        carry[item.reason] = (carry[item.reason] ?? 0) + 1;

        return carry;
    }, {});

    const undo = () => {
        setUndoing(true);

        let reverted = [];
        let skipped = [];

        // One segment per request, same as apply — undoing up to 5000 rows in a
        // single call would cost nearly as much as the apply that wrote them.
        // Replaying an already-reverted segment is inert (the service compares
        // against its pre-apply fingerprint), so if this drive stops partway —
        // a dropped response, a closed tab — the result bar is left in place
        // (see the catch below) and clicking Undo again simply restarts the
        // drive from the top rather than needing to resume a saved cursor.
        const drive = (body) =>
            axios.post(undoUrl, body).then(({ data }) => {
                reverted = reverted.concat(data.reverted ?? []);
                skipped = skipped.concat(data.skipped ?? []);

                if (data.nextCursor === null || data.nextCursor === undefined) {
                    return;
                }

                return drive({ actionId: result.actionId, cursor: data.nextCursor });
            });

        drive({ actionId: result.actionId })
            .then(() => {
                const notReverted = skipped.length;

                if (notReverted > 0) {
                    toast.warning(
                        $t('{{reverted}} rows were put back. {{skipped}} were left as they are, because they were edited since.', {
                            reverted: reverted.length,
                            skipped: notReverted,
                        }),
                    );
                } else {
                    toast.success($t('{{count}} rows were put back.', { count: reverted.length }));
                }

                onUndone(reverted);
            })
            .catch(() => toast.error($t('Could not undo the bulk apply.')))
            .finally(() => setUndoing(false));
    };

    return (
        <div className="tw-mb-3">
            <Alert type="success">
                <div className="tw-flex tw-items-center tw-gap-x-3 tw-flex-wrap">
                    <span className="tw-font-semibold">
                        {$t('{{column}} applied to {{count}} rows', { column: result.columnLabel, count: applied })}
                        {result.reconciledCount > 0 && ` · ${$t('{{count}} now reconciled', { count: result.reconciledCount })}`}
                        {skipped.length > 0 && ` · ${$t('{{count}} skipped', { count: skipped.length })}`}
                    </span>

                    {skipped.length > 0 && (
                        <TextButton onClick={() => setShowSkipped((previous) => !previous)} className="tw-text-xs tw-underline">
                            {showSkipped ? $t('Hide skipped') : $t('Show skipped')}
                        </TextButton>
                    )}

                    <div className="tw-ms-auto tw-flex tw-items-center tw-gap-x-3">
                        <TextButton onClick={undo} disabled={undoing} className="tw-flex tw-items-center tw-gap-x-1.5 tw-text-xs tw-font-semibold">
                            <ArrowUturnLeftIcon className="tw-w-3 tw-h-3" />
                            {undoing ? $t('Undoing…') : $t('Undo')}
                        </TextButton>
                        <TextButton onClick={onDismiss} className="tw-text-xs">
                            {$t('Dismiss')}
                        </TextButton>
                    </div>
                </div>

                {showSkipped && (
                    <ul className="tw-mt-2 tw-mb-0 tw-ps-4 tw-text-xs">
                        {Object.keys(skippedByReason).map((reason) => (
                            <li key={reason}>
                                {skippedByReason[reason]} — {reasons[reason] ?? reason}
                            </li>
                        ))}
                    </ul>
                )}
            </Alert>
        </div>
    );
}
